import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
} from "react-bootstrap";
import { FaDollarSign, FaBoxes, FaReceipt } from "react-icons/fa";
import Heading from "../common/Heading";
import JajotApi from "../../api/JajotApi";
import SalesReport from "./SalesReport";
import InventoryReport from "./InventoryReport";
import VouchersReport from "./VouchersReport";

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState("sales");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      outstandingPayments: 0,
      outstandingPaymentsRecords: [],
      totalLedgers: 0,
      totalLedgerBalance: 0,
      positiveLedgers: 0,
      negativeLedgers: 0,
    },
    sales: {
      totalRevenue: 0,
      ordersCount: 0,
      topProducts: [],
      orderTypeBreakdown: [],
      salesByType: [],
    },
    inventory: {
      totalItems: 0,
      lowStockItems: 0,
      outOfStockItems: 0,
      stockValue: 0,
      topItems: [],
    },
    vouchers: {
      totalVouchers: 0,
      totalAmount: 0,
      voucherTypes: [],
      recentVouchers: [],
      outstandingPayments: 0,
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const [ordersData, stockItemsData, voucherData, ledgerData] =
          await Promise.all([
            JajotApi.getOrderRecords(),
            JajotApi.getStockItemRecords(),
            JajotApi.getVoucherRecords(),
            JajotApi.getLedgerRecords(),
          ]);

        const processedData = processBusinessData(
          ordersData,
          stockItemsData,
          voucherData,
          ledgerData
        );
        setDashboardData(processedData);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  function processBusinessData(
    ordersData,
    stockItemsData,
    voucherData,
    ledgerData
  ) {
    let orders = [];
    let totalOrders = 0;
    let totalSales = 0;
    let productMap = {};

    let ledgers = [];
    let totalLedgers = 0;
    let outstandingPayments = 0;
    let outstandingPaymentsRecords = [];

    let stockItems = [];
    let totalStockItems = 0;
    let lowStockItems = 0;
    let outOfStockItems = 0;
    let stockValue = 0;
    let stockList = [];

    let vouchers = [];
    let totalVouchers = 0;
    let totalVoucherAmount = 0;
    let voucherTypeMap = {};

    let salesByType = {};

    if (ordersData && ordersData.success) {
      orders = ordersData.records;
      totalOrders = orders.length;

      for (let order of orders) {
        totalSales += parseFloat(order.Total_Amount__c) || 0;

        if (order.Order_Line_Items__r && order.Order_Line_Items__r.records) {
          for (let line of order.Order_Line_Items__r.records) {
            let productName =
              (line.Stock_Item__r && line.Stock_Item__r.Name) ||
              (line.Ledger__r && line.Ledger__r.Name) ||
              "Unknown Product";

            if (!productMap[productName]) {
              productMap[productName] = {
                name: productName,
                sales: 0,
                orders: 0,
                quantity: 0,
              };
            }

            productMap[productName].sales += parseFloat(line.Amount__c) || 0;
            productMap[productName].quantity +=
              parseFloat(line.Quantity__c) || 0;
            productMap[productName].orders += 1;
          }
        }

        let type = order.Type__c || "Other";
        if (!salesByType[type]) {
          salesByType[type] = { type: type, amount: 0, count: 0 };
        }
        salesByType[type].amount += parseFloat(order.Total_Amount__c) || 0;
        salesByType[type].count++;
      }
    }

    if (ledgerData && ledgerData.success) {
      ledgers = ledgerData.records;
      totalLedgers = ledgers.length;

      for (let ledger of ledgers) {
        let balance = parseFloat(ledger.Closing_Balance__c) || 0;
        if (balance > 0) {
          outstandingPayments += balance;
          outstandingPaymentsRecords.push({
            id: ledger.Id,
            name: ledger.Name,
            partyName: ledger.Name,
            balance: balance,
            parentGroup: ledger.Parent_Group__c || "N/A",
            state: ledger.State__c || "N/A",
            legacyAlterId: ledger.Legacy_AlterId__c || "N/A",
          });
        }
      }

      outstandingPaymentsRecords.sort((a, b) => b.balance - a.balance);
      outstandingPaymentsRecords = outstandingPaymentsRecords.slice(0, 10);
    }

    if (stockItemsData && stockItemsData.success) {
      stockItems = stockItemsData.records;
      totalStockItems = stockItems.length;

      for (let item of stockItems) {
        let qty = parseFloat(item.Closing_Quantity__c) || 0;
        let val = parseFloat(item.Closing_Amount__c) || 0;
        stockValue += val;

        if (qty === 0) outOfStockItems++;
        if (qty > 0 && qty < 50) lowStockItems++;

        stockList.push({
          name: item.Name || "Unknown Item",
          quantity: qty,
          rate: parseFloat(item.Rate__c) || 0,
          value: val,
          units: item.Units__c || "Units",
          parentGroup: item.Parent_Group__c || "N/A",
          gstApplicable: item.GST_Applicable__c,
          isTaxable: item.Is_Taxable__c,
          status:
            qty === 0 ? "Out of Stock" : qty < 50 ? "Low Stock" : "In Stock",
        });
      }
    }

    if (voucherData && voucherData.success) {
      vouchers = voucherData.records;
      totalVouchers = vouchers.length;

      for (let v of vouchers) {
        let amount = Math.abs(parseFloat(v.Amount__c) || 0);
        totalVoucherAmount += amount;

        let type = v.Type__c || "Other";
        if (!voucherTypeMap[type]) {
          voucherTypeMap[type] = { type: type, count: 0, amount: 0 };
        }
        voucherTypeMap[type].count++;
        voucherTypeMap[type].amount += amount;
      }
    }

    let topProducts = Object.values(productMap)
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);
    let topItems = stockList.sort((a, b) => b.value - a.value).slice(0, 10);
    let voucherTypes = Object.values(voucherTypeMap)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 4);
    let orderTypeBreakdown = Object.values(salesByType);

    return {
      sales: {
        totalRevenue: totalSales,
        ordersCount: totalOrders,
        topProducts,
        orderTypeBreakdown,
        salesByType: orderTypeBreakdown,
      },
      inventory: {
        totalItems: totalStockItems,
        lowStockItems,
        outOfStockItems,
        stockValue,
        topItems,
      },
      vouchers: {
        totalLedgers,
        totalVouchers,
        totalAmount: totalVoucherAmount,
        outstandingPayments,
        voucherTypes,
        outstandingPaymentsRecords,
      },
    };
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      "In Stock": "success",
      "Low Stock": "warning",
      "Out of Stock": "danger",
    };
    return <Badge bg={statusColors[status] || "secondary"}>{status}</Badge>;
  };

  const renderContent = () => {
    switch (selectedReport) {
      case "inventory":
        return (
          <InventoryReport
            dashboardData={dashboardData}
            formatCurrency={formatCurrency}
            getStatusBadge={getStatusBadge}
          />
        );
      case "vouchers":
        return (
          <VouchersReport
            dashboardData={dashboardData}
            formatCurrency={formatCurrency}
          />
        );
      default:
        return (
          <SalesReport
            dashboardData={dashboardData}
            formatCurrency={formatCurrency}
          />
        );
    }
  };

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Heading headingName="Reports Dashboard" />
      </div>

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <Spinner
              animation="border"
              role="status"
              className="me-2 mb-3"
              size="lg"
            >
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <div>
              <h5>Loading Reports Dashboard...</h5>
              <p className="text-muted">Please wait while we fetch your data</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Report Navigation */}
          <Row className="mb-4">
            <Col>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-2">
                  <div className="d-flex gap-2 flex-wrap">
                    <Button
                      variant={
                        selectedReport === "sales"
                          ? "primary"
                          : "outline-primary"
                      }
                      size="sm"
                      onClick={() => setSelectedReport("sales")}
                    >
                      <FaDollarSign className="me-1" />
                      Sales & Orders
                    </Button>
                    <Button
                      variant={
                        selectedReport === "inventory"
                          ? "primary"
                          : "outline-primary"
                      }
                      size="sm"
                      onClick={() => setSelectedReport("inventory")}
                    >
                      <FaBoxes className="me-1" />
                      Inventory
                    </Button>
                    <Button
                      variant={
                        selectedReport === "vouchers"
                          ? "primary"
                          : "outline-primary"
                      }
                      size="sm"
                      onClick={() => setSelectedReport("vouchers")}
                    >
                      <FaReceipt className="me-1" />
                      Vouchers & Payments
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Report Content */}
          {renderContent()}
        </>
      )}
    </Container>
  );
};

export default Reports;
